/**
 * Admin auth — Supabase user identity exchanged for a short-lived admin JWT.
 *
 * The frontend never decides whether an email is privileged. It signs into the
 * same Supabase project as the player app, then POSTs the Supabase access token
 * to `/v1/admin/session`; the backend verifies the JWT and applies its
 * server-owned admin allowlist before returning an admin JWT for admin APIs.
 *
 * Legacy `/v1/admin/login` remains backend-only for compatibility, but the UI
 * intentionally uses the normal Fantopy email identity model.
 */

const TOKEN_KEY = 'fantopy_admin_token';
const EMAIL_KEY = 'fantopy_admin_email';
const SUPABASE_TOKEN_KEY = 'fantopy_admin_supabase_access_token';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const DEV_TOKEN = import.meta.env.DEV ? (import.meta.env.VITE_ADMIN_DEV_TOKEN ?? '') : '';
const DEV_EMAIL = import.meta.env.DEV ? (import.meta.env.VITE_ADMIN_DEV_EMAIL ?? '') : '';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY) ?? (DEV_TOKEN || null);
}

export function getEmail(): string | null {
  return localStorage.getItem(EMAIL_KEY) ?? (DEV_EMAIL || null);
}

export function isLoggedIn(): boolean {
  const token = getToken();
  if (!token) return false;
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return false;
    const payload = JSON.parse(atob(payloadB64)) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 > Date.now();
  } catch {
    logout();
    return false;
  }
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(SUPABASE_TOKEN_KEY);
}

interface SupabaseSessionResponse {
  access_token?: string;
  user?: { email?: string };
  error?: string;
  error_description?: string;
  msg?: string;
}

interface AdminSessionResponse {
  admin: boolean;
  email: string;
  token: string;
  type: 'admin';
}

function requireSupabaseConfig(): void {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error('Admin email sign-in is not configured for this deployment.');
  }
}

async function supabaseAuth(path: string, body: unknown): Promise<SupabaseSessionResponse> {
  requireSupabaseConfig();
  const res = await fetch(`${SUPABASE_URL}/auth/v1${path}`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: ['Bearer', SUPABASE_ANON_KEY].join(' '),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as SupabaseSessionResponse;
  if (!res.ok) {
    throw new Error(data.error_description ?? data.msg ?? data.error ?? `Supabase sign-in failed (${res.status})`);
  }
  return data;
}

async function exchangeAdminSession(accessToken: string): Promise<AdminSessionResponse> {
  const res = await fetch(`${API_URL}/v1/admin/session`, {
    method: 'POST',
    headers: {
      Authorization: ['Bearer', accessToken].join(' '),
      'Content-Type': 'application/json',
    },
  });
  const data = (await res.json().catch(() => ({}))) as Partial<AdminSessionResponse> & {
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error(data.error ?? 'This Fantopy email account is not authorized for admin.');
    }
    throw new Error(data.error ?? `Admin authorization failed (${res.status})`);
  }
  if (!data.token || !data.email || data.admin !== true) {
    throw new Error('Admin authorization response was incomplete.');
  }
  return data as AdminSessionResponse;
}

async function storeAdminSession(accessToken: string): Promise<void> {
  const admin = await exchangeAdminSession(accessToken);
  localStorage.setItem(TOKEN_KEY, admin.token);
  localStorage.setItem(EMAIL_KEY, admin.email.toLowerCase());
  localStorage.setItem(SUPABASE_TOKEN_KEY, accessToken);
}

export async function loginWithPassword(email: string, password: string): Promise<void> {
  const data = await supabaseAuth('/token?grant_type=password', {
    email: email.trim().toLowerCase(),
    password,
  });
  if (!data.access_token) throw new Error('Supabase did not return an access token.');
  await storeAdminSession(data.access_token);
}

export async function sendEmailCode(email: string): Promise<void> {
  await supabaseAuth('/otp', {
    email: email.trim().toLowerCase(),
    should_create_user: false,
  });
}

export async function verifyEmailCode(email: string, token: string): Promise<void> {
  const data = await supabaseAuth('/verify', {
    email: email.trim().toLowerCase(),
    token: token.trim(),
    type: 'email',
  });
  if (!data.access_token) throw new Error('Supabase did not return an access token.');
  await storeAdminSession(data.access_token);
}
